import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Filter, 
  Lock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  FileText,
  TrendingUp,
  Calendar,
  DollarSign
} from "lucide-react";
import { format } from "date-fns";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type ValuationStatus = 'computed' | 'stale' | 'missing' | 'override-proposed' | 'override-approved' | 'locked';
type PeriodType = 'monthly' | 'quarterly' | 'annual';

interface Valuation {
  id: string;
  instrument_symbol: string;
  instrument_name: string | null;
  asset_class: string | null;
  period_type: PeriodType;
  period_date: string;
  as_of: string;
  computed_value: number | null;
  override_value: number | null;
  status: ValuationStatus;
  stale_days: number | null;
  policy: string | null;
  source_doc_id?: string | null;
  source_doc_page?: number | null;
  override_reason?: string | null;
  override_proposed_by?: string | null;
  override_proposed_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  locked: boolean | null;
  client_id: string;
  family_id: string | null;
  org_id: string;
}

// Removed mock data - now using real database queries

const getStatusBadge = (status: ValuationStatus, locked: boolean) => {
  if (locked) {
    return <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" />Locked</Badge>;
  }
  
  switch (status) {
    case 'computed':
      return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />Computed</Badge>;
    case 'stale':
      return <Badge variant="outline" className="gap-1"><AlertCircle className="h-3 w-3" />Stale</Badge>;
    case 'missing':
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Missing</Badge>;
    case 'override-proposed':
      return <Badge variant="secondary" className="gap-1"><AlertCircle className="h-3 w-3" />Override Proposed</Badge>;
    case 'override-approved':
      return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />Override Approved</Badge>;
    case 'locked':
      return <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" />Locked</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const formatCurrency = (value: number | null) => {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Valuations() {
  const { familyId, orgId } = useTenant();
  const queryClient = useQueryClient();
  const [selectedValuation, setSelectedValuation] = useState<Valuation | null>(null);
  const [periodType, setPeriodType] = useState<PeriodType>('quarterly');
  const [assetClassFilter, setAssetClassFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Fetch valuations
  const { data: valuations = [], isLoading } = useQuery({
    queryKey: ['valuations', familyId, orgId, periodType, assetClassFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('valuations')
        .select('*')
        .eq('period_type', periodType)
        .order('period_date', { ascending: false });

      if (familyId) {
        query = query.eq('family_id', familyId);
      } else if (orgId) {
        query = query.eq('org_id', orgId);
      }

      if (assetClassFilter !== 'all') {
        query = query.eq('asset_class', assetClassFilter);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Valuation[];
    },
    enabled: !!orgId,
  });

  // Fetch time series for selected valuation
  const { data: timeSeries = [] } = useQuery({
    queryKey: ['valuation-history', selectedValuation?.instrument_symbol, selectedValuation?.period_type],
    queryFn: async () => {
      if (!selectedValuation) return [];
      
      const { data, error } = await supabase
        .from('valuations')
        .select('period_date, computed_value, override_value')
        .eq('instrument_symbol', selectedValuation.instrument_symbol)
        .eq('period_type', selectedValuation.period_type)
        .order('period_date', { ascending: true })
        .limit(12);

      if (error) throw error;
      
      return data.map(d => ({
        date: format(new Date(d.period_date), 'yyyy-MM-dd'),
        value: d.override_value || d.computed_value || 0,
      }));
    },
    enabled: !!selectedValuation,
  });

  // Propose override mutation
  const proposeOverrideMutation = useMutation({
    mutationFn: async ({ id, value, reason }: { id: string; value: number; reason: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('valuations')
        .update({
          override_value: value,
          override_reason: reason,
          override_status: 'proposed',
          override_proposed_by: user?.id,
          override_proposed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valuations'] });
      toast.success('Override proposed successfully');
      setOverrideValue('');
      setOverrideReason('');
    },
    onError: (error) => {
      toast.error('Failed to propose override: ' + error.message);
    },
  });

  // Approve override mutation
  const approveOverrideMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('valuations')
        .update({
          status: 'override-approved',
          override_status: 'approved',
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valuations'] });
      toast.success('Override approved');
    },
    onError: (error) => {
      toast.error('Failed to approve override: ' + error.message);
    },
  });

  // Reject override mutation
  const rejectOverrideMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('valuations')
        .update({
          override_value: null,
          override_reason: null,
          override_status: 'rejected',
          override_proposed_by: null,
          override_proposed_at: null,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valuations'] });
      toast.success('Override rejected');
    },
    onError: (error) => {
      toast.error('Failed to reject override: ' + error.message);
    },
  });

  const handleProposeOverride = () => {
    if (!selectedValuation || !overrideValue || !overrideReason) {
      toast.error('Please enter both value and reason');
      return;
    }

    proposeOverrideMutation.mutate({
      id: selectedValuation.id,
      value: parseFloat(overrideValue),
      reason: overrideReason,
    });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left Panel - Filters */}
      <div className="w-64 border-r bg-card p-4 space-y-4 overflow-y-auto">
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Period Type</Label>
            <Select value={periodType} onValueChange={(v) => setPeriodType(v as PeriodType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Asset Class</Label>
            <Select value={assetClassFilter} onValueChange={setAssetClassFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <SelectItem value="Private Equity">Private Equity</SelectItem>
                <SelectItem value="Venture Capital">Venture Capital</SelectItem>
                <SelectItem value="Hedge Funds">Hedge Funds</SelectItem>
                <SelectItem value="Real Estate">Real Estate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="computed">Computed</SelectItem>
                <SelectItem value="stale">Stale</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
                <SelectItem value="override-proposed">Override Proposed</SelectItem>
                <SelectItem value="override-approved">Override Approved</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-2">
            <Button variant="outline" size="sm" className="w-full">
              <Calendar className="h-4 w-4" />
              Date Range
            </Button>
            <Button variant="outline" size="sm" className="w-full">
              Request Updates
            </Button>
            <Button variant="outline" size="sm" className="w-full">
              Lock Period
            </Button>
          </div>
        </div>
      </div>

      {/* Center Panel - Valuation Matrix */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-6 border-b">
          <div className="mt-2">
            <h1 className="text-2xl font-bold tracking-tight">Valuations (Marks)</h1>
            <p className="text-muted-foreground">
              Review and manage portfolio valuations across periods
            </p>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6">
            <Card>
              <CardHeader>
                <CardTitle>Valuation Matrix</CardTitle>
                <CardDescription>
                  Click any cell to view details and manage overrides
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading valuations...</div>
                ) : valuations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No valuations found</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Instrument</TableHead>
                        <TableHead>Asset Class</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>As Of</TableHead>
                        <TableHead>Policy</TableHead>
                        <TableHead className="text-right">Stale Days</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {valuations.map((valuation) => (
                        <TableRow
                          key={valuation.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedValuation(valuation)}
                        >
                          <TableCell className="font-medium">
                            <div>
                              <div>{valuation.instrument_name || valuation.instrument_symbol}</div>
                              <div className="text-xs text-muted-foreground">{valuation.instrument_symbol}</div>
                            </div>
                          </TableCell>
                          <TableCell>{valuation.asset_class || '—'}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(valuation.override_value || valuation.computed_value)}
                          </TableCell>
                          <TableCell>{getStatusBadge(valuation.status, valuation.locked || false)}</TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(valuation.as_of), 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell className="capitalize">{valuation.policy || '—'}</TableCell>
                          <TableCell className="text-right">
                            {valuation.stale_days && valuation.stale_days > 0 && (
                              <Badge variant={valuation.stale_days > 30 ? 'destructive' : 'outline'}>
                                {valuation.stale_days}d
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Valuation Inspector */}
      {selectedValuation && (
        <div className="w-96 border-l bg-card overflow-y-auto">
          <div className="p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Valuation Inspector</h2>
              <p className="text-sm text-muted-foreground">{selectedValuation.instrument_name}</p>
              <p className="text-xs text-muted-foreground">{selectedValuation.instrument_symbol}</p>
            </div>

            <Separator />

            {/* Time Series Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Valuation History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timeSeries.length > 0 ? (
                  <ChartContainer
                    config={{
                      value: {
                        label: "Value",
                        color: "hsl(var(--primary))",
                      },
                    }}
                    className="h-32"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          fontSize={10}
                          tickMargin={5}
                        />
                        <YAxis 
                          fontSize={10}
                          tickMargin={5}
                          tickFormatter={(val) => `${(val / 1000000).toFixed(0)}M`}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No historical data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Value */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Current Valuation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Computed Value</span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(selectedValuation.computed_value)}
                  </span>
                </div>
                {selectedValuation.override_value && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Override Value</span>
                    <span className="font-mono font-semibold text-orange-600">
                      {formatCurrency(selectedValuation.override_value)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">As Of</span>
                  <span className="text-sm">
                    {format(new Date(selectedValuation.as_of), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {getStatusBadge(selectedValuation.status, selectedValuation.locked)}
                </div>
              </CardContent>
            </Card>

            {/* Source Document */}
            {selectedValuation.source_doc_id && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Source Document
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" size="sm" className="w-full">
                    View Document
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Manual Override */}
            {!selectedValuation.locked && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Manual Override</CardTitle>
                  <CardDescription className="text-xs">
                    Propose a manual valuation override
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="override-value">Override Value</Label>
                    <Input
                      id="override-value"
                      type="number"
                      placeholder="Enter value"
                      value={overrideValue}
                      onChange={(e) => setOverrideValue(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="override-reason">Reason</Label>
                    <Textarea
                      id="override-reason"
                      placeholder="Explain the reason for this override"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={handleProposeOverride}
                    disabled={!overrideValue || !overrideReason || proposeOverrideMutation.isPending}
                  >
                    {proposeOverrideMutation.isPending ? 'Proposing...' : 'Propose Override'}
                  </Button>

                  {selectedValuation.status === 'override-proposed' && (
                    <div className="flex gap-2 mt-2">
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => approveOverrideMutation.mutate(selectedValuation.id)}
                        disabled={approveOverrideMutation.isPending}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => rejectOverrideMutation.mutate(selectedValuation.id)}
                        disabled={rejectOverrideMutation.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {selectedValuation.override_reason && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Override Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <span className="text-xs text-muted-foreground">Reason:</span>
                    <p className="text-sm mt-1">{selectedValuation.override_reason}</p>
                  </div>
                  {selectedValuation.override_proposed_at && (
                    <div>
                      <span className="text-xs text-muted-foreground">Proposed:</span>
                      <p className="text-sm">{format(new Date(selectedValuation.override_proposed_at), 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                  )}
                  {selectedValuation.approved_at && (
                    <div>
                      <span className="text-xs text-muted-foreground">Approved:</span>
                      <p className="text-sm">{format(new Date(selectedValuation.approved_at), 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => setSelectedValuation(null)}
            >
              Close Inspector
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
